/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Upload, X } from 'lucide-react'

import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  useOwnerSettingsQuery,
  useUpdateOwnerSettingsMutation,
} from '@/hooks/useOwnerSettings'
import type { InvoicePrintFormat } from '@/types/backend'
import apiClient from '@/apiClient'

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2 MB

const schema = z.object({
  printFormat: z.enum(['A4', 'THERMAL_80', 'THERMAL_58']),
  businessName: z.string().max(120, '120 caractères max').optional(),
  businessPhone: z.string().max(32, '32 caractères max').optional(),
  businessAddress: z.string().max(200, '200 caractères max').optional(),
  logoUrl: z.string().max(500, '500 caractères max').optional(),
})

type FormValues = z.infer<typeof schema>

type UploadResponse = {
  url: string
}

export default function OwnerInvoiceSettingsCard() {
  const { data: settings } = useOwnerSettingsQuery(true)
  const updateSettings = useUpdateOwnerSettingsMutation()

  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const invoice = settings?.invoice

  const initialValues = useMemo<FormValues>(
    () => ({
      printFormat: (invoice?.printFormat as InvoicePrintFormat) || 'A4',
      businessName: invoice?.businessName || '',
      businessPhone: invoice?.businessPhone || '',
      businessAddress: invoice?.businessAddress || '',
      logoUrl: invoice?.logoUrl || '',
    }),
    [
      invoice?.printFormat,
      invoice?.businessName,
      invoice?.businessPhone,
      invoice?.businessAddress,
      invoice?.logoUrl,
    ]
  )

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues,
  })

  useEffect(() => {
    form.reset(initialValues)
  }, [form, initialValues])

  const values = form.watch()

  const isDirty =
    values.printFormat !== initialValues.printFormat ||
    (values.businessName || '') !== (initialValues.businessName || '') ||
    (values.businessPhone || '') !== (initialValues.businessPhone || '') ||
    (values.businessAddress || '') !== (initialValues.businessAddress || '') ||
    (values.logoUrl || '') !== (initialValues.logoUrl || '')

  const onSubmit = async (data: FormValues) => {
    try {
      await updateSettings.mutateAsync({
        invoice: {
          printFormat: data.printFormat,
          businessName: data.businessName?.trim() || '',
          businessPhone: data.businessPhone?.trim() || '',
          businessAddress: data.businessAddress?.trim() || '',
          logoUrl: data.logoUrl?.trim() || '',
        },
      })

      toast.success('Paramètres de facture enregistrés')
    } catch (e: any) {
      toast.error("Impossible d'enregistrer", {
        description: e?.response?.data?.message ?? 'Erreur inconnue',
      })
    }
  }

  const handlePickLogo = () => {
    fileInputRef.current?.click()
  }

  const handleRemoveLogo = () => {
    form.setValue('logoUrl', '', { shouldDirty: true })
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''

    if (!file) return

    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error('Format non supporté', {
        description: 'Utilise PNG, JPG ou WebP',
      })
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error('Fichier trop volumineux', {
        description: 'Taille max : 2 MB',
      })
      return
    }

    try {
      setIsUploading(true)

      const formData = new FormData()
      formData.append('file', file)
      formData.append('folder', 'invoice-logos')

      const { data } = await apiClient.post<UploadResponse>(
        '/owners/me/invoice-logo',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      if (!data?.url) {
        throw new Error("URL d'image manquante")
      }

      form.setValue('logoUrl', data.url, { shouldDirty: true })
      toast.success('Logo téléversé')
    } catch (e: any) {
      toast.error('Échec du téléversement', {
        description: e?.response?.data?.message ?? 'Erreur inconnue',
      })
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <h2 className='text-xl font-semibold'>Identité de facture</h2>
        <p className='text-sm text-muted-foreground'>
          Configure le format d’impression, les informations affichées sur la
          facture et le logo du commerce.
        </p>
      </CardHeader>

      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-5'>
            <FormField
              control={form.control}
              name='printFormat'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Format d’impression par défaut</FormLabel>
                  <FormControl>
                    <select
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                    >
                      <option value='A4'>A4</option>
                      <option value='THERMAL_80'>Thermique 80 mm</option>
                      <option value='THERMAL_58'>Thermique 58 mm</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='grid gap-4 md:grid-cols-2'>
              <FormField
                control={form.control}
                name='businessName'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nom du commerce</FormLabel>
                    <FormControl>
                      <Input placeholder='Ex: Superette Kénéya' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='businessPhone'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Téléphone</FormLabel>
                    <FormControl>
                      <Input placeholder='Ex: +2250700000000' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='businessAddress'
                render={({ field }) => (
                  <FormItem className='md:col-span-2'>
                    <FormLabel>Adresse</FormLabel>
                    <FormControl>
                      <Input
                        placeholder='Ex: Korhogo, Quartier Résidentiel'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name='logoUrl'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Logo du commerce</FormLabel>
                  <FormControl>
                    <div className='space-y-3'>
                      <input
                        ref={fileInputRef}
                        type='file'
                        accept='image/png,image/jpeg,image/webp'
                        className='hidden'
                        onChange={handleFileChange}
                      />

                      <div className='flex flex-wrap items-center gap-3'>
                        <Button
                          type='button'
                          variant='outline'
                          onClick={handlePickLogo}
                          disabled={isUploading}
                        >
                          <Upload className='mr-2 h-4 w-4' />
                          {isUploading
                            ? 'Téléversement…'
                            : 'Téléverser un logo'}
                        </Button>

                        {field.value ? (
                          <Button
                            type='button'
                            variant='ghost'
                            onClick={handleRemoveLogo}
                          >
                            <X className='mr-2 h-4 w-4' />
                            Retirer le logo
                          </Button>
                        ) : null}
                      </div>

                      <Input
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder='https://... ou /uploads/invoice-logos/...'
                      />

                      <p className='text-xs text-muted-foreground'>
                        Conditions : PNG, JPG ou WebP. Taille max 2 MB. Logo
                        simple, lisible en petit, fond clair ou transparent,
                        ratio carré ou horizontal recommandé.
                      </p>

                      {field.value ? (
                        <div className='rounded-md border p-3'>
                          <div className='text-xs text-muted-foreground mb-2'>
                            Aperçu
                          </div>
                          <img
                            src={field.value}
                            alt='Logo facture'
                            className='h-20 w-auto object-contain'
                            crossOrigin='anonymous'
                          />
                        </div>
                      ) : null}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='flex justify-end'>
              <Button
                type='submit'
                disabled={!isDirty || updateSettings.isPending}
              >
                {updateSettings.isPending ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
